import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

// Kebab (⋮) menu whose list renders in a portal on <body>, positioned against the viewport.
// Living inside a table cell it would otherwise be clipped by the `.table-responsive`
// scroll container (overflow-x: auto also clips vertically) — which cuts the menu off on a
// short table, e.g. a single row on a phone. It flips upward when there's no room below and
// scrolls internally if it's taller than the screen, so every item is always reachable.

const MARGIN = 8;
const MenuContext = createContext({ close: () => {} });

export default function ActionMenu({ children, title = 'Actions' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const hasItems = React.Children.toArray(children).length > 0;

  const reposition = useCallback(() => {
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;
    const r = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxHeight = vh - MARGIN * 2;
    const menuW = menu.offsetWidth;
    const menuH = Math.min(menu.scrollHeight, maxHeight);
    const left = Math.max(MARGIN, Math.min(r.right - menuW, vw - menuW - MARGIN));
    let top;
    if (menuH <= vh - r.bottom - MARGIN) top = r.bottom + 4;
    else if (menuH <= r.top - MARGIN) top = r.top - menuH - 4;
    else top = Math.max(MARGIN, Math.min(r.bottom + 4, vh - menuH - MARGIN));
    setPos({ top, left, maxHeight });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
    else setPos(null);
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return undefined;
    const inside = (target) => menuRef.current?.contains(target) || btnRef.current?.contains(target);
    const onPointerDown = (e) => { if (!inside(e.target)) setOpen(false); };
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    // Follow the button while anything scrolls (the table scrolls sideways on a phone) rather
    // than closing on every scroll event — only close once the button itself has left the screen.
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      const r = btnRef.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) setOpen(false);
      else reposition();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  if (!hasItems) return <span className="text-muted">—</span>;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`btn btn-outline-secondary btn-sm border-0 px-2${open ? ' active' : ''}`}
        style={{ fontWeight: 700, fontSize: 18, lineHeight: 1 }}
        title={title}
        aria-label={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        ⋮
      </button>
      {open && ReactDOM.createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="dropdown-menu show shadow"
          style={{
            position: 'fixed',
            right: 'auto',
            bottom: 'auto',
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            margin: 0,
            zIndex: 1060,
            minWidth: 230,
            maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
            maxHeight: pos?.maxHeight,
            overflowY: 'auto',
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <MenuContext.Provider value={{ close }}>{children}</MenuContext.Provider>
        </div>,
        document.body
      )}
    </>
  );
}

function Item({ icon, onClick, href, target, rel, disabled, className = '', children }) {
  const { close } = useContext(MenuContext);
  const style = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', whiteSpace: 'normal' };
  const content = (
    <>
      {icon != null && <span style={{ minWidth: 28, textAlign: 'center', flexShrink: 0 }}>{icon}</span>}
      <span>{children}</span>
    </>
  );
  if (href && !disabled) {
    return (
      <a role="menuitem" className={`dropdown-item ${className}`} style={style} href={href} target={target} rel={rel} onClick={close}>
        {content}
      </a>
    );
  }
  return (
    <button
      type="button"
      role="menuitem"
      className={`dropdown-item ${className}`}
      style={style}
      disabled={disabled}
      onClick={() => { close(); if (onClick) onClick(); }}
    >
      {content}
    </button>
  );
}

function Divider() {
  return <div className="dropdown-divider" role="separator" />;
}

ActionMenu.Item = Item;
ActionMenu.Divider = Divider;
